import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

interface AuthContextType {
  user: { email: string; role: string } | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const API_BASE = import.meta.env.VITE_API_URL || 'https://sggadgets-backend.aadhithyanannamalai.workers.dev';

function getToken(): string | null {
  return localStorage.getItem('auth_token');
}

function setToken(token: string): void {
  localStorage.setItem('auth_token', token);
}

function clearToken(): void {
  localStorage.removeItem('auth_token');
}

async function decodeToken(token: string): Promise<{ email: string; role: string; exp: number }> {
  const payload = JSON.parse(atob(token.split('.')[1]));
  return payload;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<{ email: string; role: string } | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    const token = getToken();
    if (!token) return false;

    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        clearToken();
        setUser(null);
        return false;
      }

      const data = await res.json();
      setToken(data.token);
      const decoded = await decodeToken(data.token);
      setUser({ email: decoded.email, role: decoded.role });
      return true;
    } catch {
      clearToken();
      setUser(null);
      return false;
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const token = getToken();
      if (token) {
        try {
          const decoded = await decodeToken(token);
          if (decoded.exp * 1000 < Date.now()) {
            const refreshed = await refreshToken();
            if (!refreshed) {
              setLoading(false);
              return;
            }
          } else {
            setUser({ email: decoded.email, role: decoded.role });
          }
        } catch {
          clearToken();
        }
      }
      setLoading(false);
    };
    init();
  }, [refreshToken]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || 'Login failed');
    }

    const data = await res.json();
    setToken(data.token);
    const decoded = await decodeToken(data.token);
    setUser({ email: decoded.email, role: decoded.role });
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        loading,
        login,
        logout,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
